import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, Calendar, Clock, Bell, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mantenemos esto por si hay citas muy viejas guardadas con los códigos anteriores
const SERVICE_NAMES = {
  cerquillo: "Cerquillo",
  corte_completo: "Corte Completo"
};

const STATUS_LABELS = {
  pending: "Pendiente",
  confirmed: "Confirmada",
  completed: "Completada",
  cancelled: "Cancelada"
};

const ClientStatusPage = () => {
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState(searchParams.get("phone") || "");
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (searchParams.get("phone")) {
      handleSearch();
    }
  }, []);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    
    if (!phone.trim()) {
      toast.error("Por favor ingresa tu número de teléfono");
      return;
    }

    setLoading(true);
    try {
      // 🛠️ FIX 1: Agregamos la palabra "public" a la ruta para que conecte con el servidor
      const response = await axios.get(`${API}/public/appointments/${phone}`);
      setAppointments(response.data);
      setSearched(true);
      if (response.data.length === 0) {
        toast.info("No se encontraron citas con este número");
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Error al buscar citas");
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: "status-pending",
      confirmed: "status-confirmed",
      completed: "status-completed",
      cancelled: "status-cancelled"
    };
    return classes[status] || "status-pending";
  };

  const getCardClass = (status) => {
    return `appointment-card ${status}`;
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        {/* CORRECCIÓN: pt-14 pb-4 para empujar el contenido debajo del reloj del iPhone */}
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {/* CORRECCIÓN: Logo por defecto en lugar de emojis */}
            <img src="/logo192.png" alt="Barbershop Logo" className="w-10 h-10 object-contain rounded-lg" />
            <span className="font-heading text-2xl font-bold uppercase tracking-wider text-white">
              Barbershop
            </span>
          </Link>
          <Link to="/">
            <Button 
              variant="ghost" 
              className="text-zinc-400 hover:text-white font-heading uppercase tracking-wider"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-2">
          <img src="/logo192.png" alt="Barbershop Logo" className="w-16 h-16 object-contain rounded-xl" />
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-white text-center mb-2">
          Mi Cita
        </h1>
        <p className="text-zinc-400 text-center mb-10">
          Ingresa tu número de teléfono para ver tus citas
        </p>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-12" data-testid="phone-search-form">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Tu número de teléfono"
                className="input-field h-14 pl-12 text-lg"
                data-testid="phone-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase tracking-wider font-bold h-14 px-8"
              data-testid="search-btn"
            >
              <Search className="w-5 h-5 md:mr-2" />
              <span className="hidden md:inline">Buscar</span>
            </Button>
          </div>
        </form>

        {/* Appointments List */}
        {searched && (
          <div data-testid="appointments-list">
            {appointments.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold uppercase text-zinc-500 mb-2">
                  No hay citas
                </h3>
                <p className="text-zinc-600 mb-6">
                  No encontramos citas asociadas a este número
                </p>
                <Link to="/reservar">
                  <Button className="bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase tracking-wider font-bold">
                    Reservar Ahora
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <h2 className="font-heading text-lg font-bold uppercase text-zinc-400 mb-6">
                  Tus Citas ({appointments.length})
                </h2>
                {appointments.map((apt) => (
                  <div 
                    key={apt.id} 
                    className={getCardClass(apt.status)}
                    data-testid={`appointment-${apt.id}`}
                  >
                    <div className="pl-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`status-badge ${getStatusClass(apt.status)}`}>
                          {STATUS_LABELS[apt.status]}
                        </span>
                        <span className="font-heading text-xl font-bold text-amber-500">
                          {apt.price} DOP
                        </span>
                      </div>
                      
                      {/* 🛠️ FIX 2: Mostramos el nombre dinámico del servicio, o el antiguo si es muy viejo */}
                      <h3 className="font-heading text-lg font-bold uppercase text-white mb-2">
                        {SERVICE_NAMES[apt.service] || apt.service}
                      </h3>
                      
                      <div className="flex flex-wrap gap-4 text-zinc-400 text-sm">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-amber-500" />
                          {format(parseISO(apt.date), "EEEE d 'de' MMMM", { locale: es })}
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-2 text-amber-500" />
                          {apt.time}
                        </span>
                      </div>

                      {/* Queue position for active appointments */}
                      {(apt.status === "pending" || apt.status === "confirmed") && (
                        <div className="bg-amber-500/10 p-3 border border-amber-500/20 mt-4">
                          <div className="flex items-center justify-between">
                            <span className="flex items-center text-amber-500 text-sm">
                              <Users className="w-4 h-4 mr-2" />
                              Personas delante de ti:
                            </span>
                            <span className="font-heading text-2xl font-bold text-amber-500">
                              {apt.queue_position}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Notifications */}
                      {apt.notifications && apt.notifications.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-zinc-800">
                          <h4 className="flex items-center text-sm font-heading uppercase text-amber-500 mb-3">
                            <Bell className="w-4 h-4 mr-2" />
                            Notificaciones
                          </h4>
                          <div className="space-y-2">
                            {apt.notifications.map((notif, idx) => (
                              <div 
                                key={idx}
                                className="bg-zinc-800/50 border-l-2 border-amber-500 p-3 text-sm"
                              >
                                <p className="text-zinc-300">{notif.message}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-zinc-600 text-xs">
                                    {format(parseISO(notif.sent_at), "d MMM, HH:mm", { locale: es })}
                                  </span>
                                  {notif.whatsapp_sent && (
                                    <span className="text-green-500 text-xs">✓ WhatsApp enviado</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Not searched yet */}
        {!searched && (
          <div className="text-center py-16">
            <Phone className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">
              Ingresa tu número de teléfono para buscar tus citas
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientStatusPage;